import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SupabaseClient } from "@supabase/supabase-js";
import { AuthScreen } from "../features/auth/AuthScreen";
import {
  clearQuickReturnUserId,
  getQuickReturnUserId,
  setQuickReturnUserId
} from "../features/auth/quickReturnSession";
import { useAuthSession } from "../features/auth/useAuthSession";
import { BattleScreen } from "../features/battle/components/BattleScreen";
import {
  finishBattleSession,
  submitBattleAnswer
} from "../features/battle/data/battleApi";
import type { BattleReward } from "../features/battle/data/battleApi";
import type { BattleQuestion } from "../features/battle/domain/battleMachine";
import { CampaignLevelPicker } from "../features/campaign/CampaignLevelPicker";
import { LobbyScreen } from "../features/lobby/LobbyScreen";
import type { BattleSetup } from "../features/lobby/LobbyScreen";
import { appDataClient } from "../shared/lib/supabase";
import type { CampaignLevelRow, Database } from "../shared/types/database";

export function App() {
  const searchParams = new URLSearchParams(window.location.search);
  const demoMode = import.meta.env.VITE_ENABLE_DEMO_MODE === "true"
    && searchParams.get("demo") === "1";

  if (appDataClient.kind === "mock" || demoMode) {
    const campaignParam = searchParams.get("campaign");
    const campaignDemoLevel = Math.min(
      20,
      Math.max(1, Number(campaignParam) || 1)
    );
    if (campaignParam !== null) {
      return (
        <main className="onboarding-shell onboarding-shell--lobby">
          <section className="lobby-panel">
            <h2>Путь героя</h2>
            <CampaignLevelPicker
              completedLevels={Array.from(
                { length: campaignDemoLevel - 1 },
                (_, index) => index + 1
              )}
              currentLevel={campaignDemoLevel}
              isUzbek={false}
              levels={DEMO_CAMPAIGN_LEVELS}
              selectedLevel={campaignDemoLevel}
              onSelect={() => undefined}
            />
          </section>
        </main>
      );
    }

    const multipleChoiceDemo = searchParams.get("scenario") === "multiple";
    const longQuestionDemo = searchParams.get("scenario") === "long";
    const mageDemo = searchParams.get("enemy") === "mage";
    return (
      <BattleScreen
        playerName="Oleksandr"
        opponent={mageDemo
          ? {
            attackStyle: "magic",
            isBoss: false,
            name: "Soya sehrgari",
            tier: 1,
            variant: 2
          }
          : {
            attackStyle: "ranged",
            isBoss: false,
            name: "Skelet kamonchi",
            tier: 2,
            variant: 1
          }}
        questions={longQuestionDemo
          ? LONG_QUESTION_DEMO
          : multipleChoiceDemo
            ? MULTIPLE_CHOICE_DEMO
            : undefined}
        subjectTitle={longQuestionDemo
          ? "История"
          : multipleChoiceDemo
            ? "География"
            : undefined}
        reward={DEMO_REWARD}
        onAbandonBattle={() => Promise.resolve()}
      />
    );
  }

  return <ConnectedApp client={appDataClient.client} />;
}

const DEMO_ENEMY_NAMES = [
  "Скелет-страж",
  "Скелет-лучник",
  "Маг тени",
  "Гоблин-капитан",
  "Ледяной голем",
  "Огненный бесёнок",
  "Тёмный рыцарь",
  "Лесной шаман"
] as const;

const DEMO_CAMPAIGN_LEVELS: readonly CampaignLevelRow[] = Array.from(
  { length: 20 },
  (_, index) => ({
    attack_style: index % 3 === 1 ? "ranged" : index % 3 === 2 ? "magic" : "melee",
    created_at: "2026-08-01T00:00:00.000Z",
    difficulty: index < 6 ? 1 : index < 13 ? 2 : 3,
    enemy_key: `enemy-${index % 8}`,
    enemy_name_ru: DEMO_ENEMY_NAMES[index % DEMO_ENEMY_NAMES.length],
    enemy_name_uz: DEMO_ENEMY_NAMES[index % DEMO_ENEMY_NAMES.length],
    enemy_tier: Math.floor(index / 5) + 1,
    enemy_variant: index % 8,
    is_boss: (index + 1) % 5 === 0,
    level_no: index + 1,
    opponent_hp: 65 + index * 10
  })
);

const MULTIPLE_CHOICE_DEMO: readonly BattleQuestion[] = [{
  id: "demo-central-asia",
  slug: "central-asia-countries",
  prompt: ["Выбери все страны Центральной Азии."],
  spokenPrompt: "Выбери все страны Центральной Азии.",
  questionType: "multiple_choice",
  difficulty: 2,
  timeLimitSeconds: 25,
  options: [
    { id: "uzbekistan", label: "Узбекистан" },
    { id: "kazakhstan", label: "Казахстан" },
    { id: "japan", label: "Япония" },
    { id: "brazil", label: "Бразилия" }
  ],
  correctOptionIds: ["uzbekistan", "kazakhstan"]
}];

const LONG_QUESTION_DEMO: readonly BattleQuestion[] = [{
  id: "demo-democracy-athens",
  slug: "democracy-athens",
  prompt: ["В каком древнем городе возникла демократия?"],
  spokenPrompt: "В каком древнем городе возникла демократия?",
  questionType: "single_choice",
  difficulty: 1,
  timeLimitSeconds: 20,
  options: [
    { id: "athens", label: "Афины" },
    { id: "sparta", label: "Спарта" },
    { id: "rome", label: "Рим" },
    { id: "babylon", label: "Вавилон" }
  ],
  correctOptionIds: ["athens"]
}];

const DEMO_REWARD: BattleReward = {
  status: "victory",
  xpEarned: 105,
  coinsEarned: 36,
  correctAnswers: 4,
  totalAnswers: 4,
  maxCombo: 4,
  heroLevelBefore: 4,
  heroLevelAfter: 5,
  levelUp: true,
  experienceAfter: 604,
  xpIntoLevel: 54,
  xpForNextLevel: 200,
  upgradePointsEarned: 1,
  upgradePointsAvailable: 1,
  totalCoins: 36,
  currentStreak: 1,
  bestStreak: 1,
  campaignLevel: 4,
  campaignLevelUnlocked: 5,
  campaignCompleted: false,
  isReplay: false,
  firstCompletion: true
};

function ConnectedApp({ client }: { client: SupabaseClient<Database> }) {
  const { i18n } = useTranslation();
  const { loading, session } = useAuthSession(client);
  const [battle, setBattle] = useState<BattleSetup | null>(null);
  const [reward, setReward] = useState<BattleReward | null>(null);
  const [rewardLoading, setRewardLoading] = useState(false);
  const [quickReturnUserId, setQuickReturnUserIdState] = useState(getQuickReturnUserId);

  useEffect(() => {
    if (session && quickReturnUserId && quickReturnUserId !== session.user.id) {
      clearQuickReturnUserId();
      setQuickReturnUserIdState(null);
    }
  }, [quickReturnUserId, session]);

  const completeBattle = useCallback(async ({ phase }: {
    phase: "won" | "lost";
  }) => {
    if (!battle) {
      return;
    }

    setRewardLoading(true);
    try {
      const result = await finishBattleSession(
        client,
        battle.sessionId,
        phase === "lost" ? "timeout" : "terminal"
      );
      setReward(result);
    } catch {
      console.error("Unable to finish the battle session.");
    } finally {
      setRewardLoading(false);
    }
  }, [battle, client]);

  const abandonBattle = useCallback(async () => {
    if (!battle) {
      return;
    }

    const abandonedSessionId = battle.sessionId;
    await finishBattleSession(client, abandonedSessionId, "abandoned");
    setReward(null);
    setBattle((current) => (
      current?.sessionId === abandonedSessionId ? null : current
    ));
  }, [battle, client]);

  const lockToReturningHero = useCallback(() => {
    if (!session) {
      return;
    }

    setQuickReturnUserId(session.user.id);
    setQuickReturnUserIdState(session.user.id);
    setReward(null);
    setBattle(null);
  }, [session]);

  const continueActiveSession = useCallback(() => {
    clearQuickReturnUserId();
    setQuickReturnUserIdState(null);
  }, []);

  const logoutForCredentials = useCallback(async () => {
    const { error } = await client.auth.signOut({ scope: "local" });
    if (error) {
      throw error;
    }

    clearQuickReturnUserId();
    setQuickReturnUserIdState(null);
  }, [client]);

  if (loading) {
    return <AppLoading />;
  }

  if (!session) {
    return <AuthScreen client={client} />;
  }

  if (quickReturnUserId === session.user.id) {
    return (
      <AuthScreen
        activeSessionEmail={session.user.email}
        client={client}
        onFullLogout={logoutForCredentials}
        onQuickReturn={continueActiveSession}
      />
    );
  }

  if (!battle) {
    return (
      <LobbyScreen
        client={client}
        session={session}
        onLogout={lockToReturningHero}
        onStart={(setup) => {
          setReward(null);
          setBattle(setup);
        }}
      />
    );
  }

  const playerName = battle.playerName;
  const isUzbek = i18n.resolvedLanguage?.startsWith("uz") ?? true;

  return (
    <BattleScreen
      playerName={playerName || (isUzbek ? battle.hero.name_uz : battle.hero.name_ru)}
      opponent={{
        attackStyle: battle.opponent.attackStyle,
        isBoss: battle.opponent.isBoss,
        name: isUzbek ? battle.opponent.nameUz : battle.opponent.nameRu,
        tier: battle.opponent.tier,
        variant: battle.opponent.variant
      }}
      questions={battle.questions}
      configuration={battle.configuration}
      subjectTitle={isUzbek ? battle.subject.title_uz : battle.subject.title_ru}
      reward={reward}
      rewardLoading={rewardLoading}
      onSubmitAnswer={(questionId, questionIndex, selectedOptionIds, responseTimeMs, useSpecial) => (
        submitBattleAnswer(
          client,
          battle.sessionId,
          questionId,
          questionIndex,
          selectedOptionIds,
          responseTimeMs,
          useSpecial
        )
      )}
      onBattleComplete={(result) => {
        void completeBattle(result);
      }}
      onAbandonBattle={abandonBattle}
      onLeave={() => {
        setReward(null);
        setBattle(null);
      }}
    />
  );
}

function AppLoading() {
  const { t } = useTranslation();
  return (
    <main className="onboarding-shell">
      <div className="onboarding-loading" role="status">
        <span />
        {t("authLoading")}
      </div>
    </main>
  );
}
